import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';
import Swal from 'sweetalert2';
import { Icon } from '@iconify/react';
import { Modal, Button, Card } from 'react-bootstrap';
import PlanDetailCard from './PlanDetailCard';

const SubscriptionManager = () => {
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showRenewModal, setShowRenewModal] = useState(false);
    const [plans, setPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [renewingId, setRenewingId] = useState(null);

    useEffect(() => {
        fetchSubscription();
    }, []);

    const fetchSubscription = async () => {
        try {
            const response = await api.get('/clinic/subscription');
            setSubscription(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching subscription:', error);
            setLoading(false);
        }
    };

    const fetchPlans = async () => {
        try {
            const response = await api.get('/public/plans');
            setPlans(response.data);
        } catch (error) {
            console.error('Error fetching plans', error);
        }
    };

    const handleRenewClick = () => {
        fetchPlans();
        setShowRenewModal(true);
    };

    const loadRazorpay = () => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handlePayment = async (plan) => {
        setRenewingId(plan._id);

        const res = await loadRazorpay();
        if (!res) {
            Swal.fire('Error', 'Razorpay SDK failed to load. Are you online?', 'error');
            setRenewingId(null);
            return;
        }

        try {
            // 1. Create Order
            const orderRes = await api.post('/clinic/renew-order', { planId: plan._id });

            const options = {
                key: orderRes.data.keyId,
                amount: orderRes.data.amount,
                currency: orderRes.data.currency,
                name: "Medoro Health",
                description: `Extend Subscription - ${plan.name}`,
                order_id: orderRes.data.id,
                handler: async function (response) {
                    try {
                        Swal.fire({
                            title: 'Processing Extension...',
                            text: 'Please wait while we verify payment.',
                            allowOutsideClick: false,
                            didOpen: () => Swal.showLoading()
                        });

                        // 2. Verify Payment
                        await api.post('/clinic/renew-verify', {
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_signature: response.razorpay_signature,
                            planName: plan.name
                        });

                        Swal.fire('Success!', 'Your subscription has been extended successfully.', 'success');
                        setShowRenewModal(false);
                        fetchSubscription(); // Refresh Data
                    } catch (verifyError) {
                        console.error(verifyError);
                        Swal.fire('Error', 'Payment verification failed. Please contact support.', 'error');
                    }
                    setRenewingId(null);
                },
                prefill: {
                    name: subscription?.adminName || "Clinic Admin",
                    email: subscription?.adminEmail || "",
                    contact: subscription?.adminMobile || ""
                },
                theme: { color: "#3399cc" },
                modal: {
                    ondismiss: function () {
                        Swal.fire('Cancelled', 'Transaction cancelled', 'error');
                        setRenewingId(null);
                    }
                }
            };

            const rzp1 = new window.Razorpay(options);
            rzp1.on('payment.failed', function (response) {
                Swal.fire('Payment Failed', response.error.description, 'error');
                setRenewingId(null);
            });
            rzp1.open();

        } catch (error) {
            console.error('Renewal Error:', error);
            Swal.fire('Error', 'Failed to initiate extension', 'error');
            setRenewingId(null);
        }
    };

    if (loading) return <div className="text-center p-5">Loading subscription details...</div>;

    if (!subscription) return <div className="text-center p-5 text-danger">Failed to load subscription info.</div>;

    const { planName, expiryDate, daysRemaining, status } = subscription;
    const isActive = daysRemaining > 0;
    const isActiveForRenew = daysRemaining <= 2 || status === 'Inactive';

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h5 className="mb-0">My Subscription</h5>
            </div>
            <div className="card-body p-24">

                {/* Subscription Status Banner */}
                <div className={`p-4 radius-8 mb-4 border ${isActive ? 'bg-primary-50 border-primary-100' : 'bg-danger-50 border-danger-100'}`}>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className={`w-64-px h-64-px radius-circle d-flex align-items-center justify-content-center text-3xl ${isActive ? 'bg-primary-100 text-primary-600' : 'bg-danger-100 text-danger-600'}`}>
                                <Icon icon={isActive ? "solar:crown-star-bold" : "solar:close-circle-bold"} />
                            </div>
                            <div>
                                <h4 className="mb-1 text-dark fw-bold">{planName} Plan</h4>
                                <p className={`mb-0 fw-medium ${isActive ? 'text-primary-600' : 'text-danger-600'}`}>
                                    {isActive ? 'Active' : 'Expired'}
                                    <span className="text-secondary fw-normal ms-2">
                                        (Expires on: {new Date(expiryDate).toLocaleDateString()})
                                    </span>
                                </p>
                            </div>
                        </div>

                        {/* Renew Button - Only Visible if needed */}
                        {isActiveForRenew && (
                            <button
                                onClick={handleRenewClick}
                                className="btn text-white fw-bold px-32 py-12 radius-8 d-flex align-items-center gap-2 transition-all hover-scale"
                                style={{ backgroundColor: '#2E7D32', borderColor: '#2E7D32' }} // Dark Tea Green
                            >
                                <Icon icon="solar:refresh-circle-bold" className="text-xl" />
                                Extend Subscription
                            </button>
                        )}
                    </div>
                </div>

                <div className="row g-4">
                    {/* Details Card */}
                    <div className="col-md-6">
                        <div className="card h-100 border shadow-none bg-base">
                            <div className="card-body">
                                <h6 className="mb-3 fw-bold text-secondary">Plan Details</h6>
                                <ul className="list-group list-group-flush">
                                    <li className="list-group-item bg-transparent px-0 d-flex justify-content-between">
                                        <span className="text-secondary">Plan Cost</span>
                                        <span className="fw-bolder text-dark">
                                            {subscription.planDetails?.currency === 'INR' ? '₹' : '$'}{subscription.planDetails?.price}
                                        </span>
                                    </li>
                                    <li className="list-group-item bg-transparent px-0 d-flex justify-content-between">
                                        <span className="text-secondary">Start Date</span>
                                        <span className="fw-medium">
                                            {subscription.startDate ? new Date(subscription.startDate).toDateString() : '-'}
                                        </span>
                                    </li>
                                    <li className="list-group-item bg-transparent px-0 d-flex justify-content-between">
                                        <span className="text-secondary">End Date</span>
                                        <span className="fw-medium text-dark">{new Date(expiryDate).toDateString()}</span>
                                    </li>
                                    <li className="list-group-item bg-transparent px-0 d-flex justify-content-between">
                                        <span className="text-secondary">Status</span>
                                        <span className={`badge ${status === 'Active' ? 'bg-success' : 'bg-danger'}`}>{status}</span>
                                    </li>
                                    <li className="list-group-item bg-transparent px-0 d-flex justify-content-between">
                                        <span className="text-secondary">Days Remaining</span>
                                        <span className={`fw-bold ${daysRemaining < 5 ? 'text-danger' : 'text-success'}`}>{daysRemaining > 0 ? daysRemaining : 0} Days</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="card h-100 border shadow-none bg-base">
                            <div className="card-body">
                                <h6 className="mb-3 fw-bold text-secondary">Plan Limits & Features</h6>

                                {/* Limits Section */}
                                <div className="d-flex flex-column gap-3 mb-4">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="w-40-px h-40-px radius-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                                            <Icon icon="solar:stethoscope-bold" className="text-xl" />
                                        </div>
                                        <div>
                                            <p className="mb-0 text-secondary text-sm">Max Doctors</p>
                                            <h6 className="fw-bold mb-0 text-dark">
                                                {subscription.planDetails?.limits?.maxDoctors === 0 ? 'Unlimited' : subscription.planDetails?.limits?.maxDoctors}
                                            </h6>
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-center gap-3">
                                        <div className="w-40-px h-40-px radius-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                                            <Icon icon="solar:users-group-rounded-bold" className="text-xl" />
                                        </div>
                                        <div>
                                            <p className="mb-0 text-secondary text-sm">Max Staff</p>
                                            <h6 className="fw-bold mb-0 text-dark">
                                                {subscription.planDetails?.limits?.maxStaff === 0 ? 'Unlimited' : subscription.planDetails?.limits?.maxStaff}
                                            </h6>
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-center gap-3">
                                        <div className="w-40-px h-40-px radius-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                                            <Icon icon="solar:database-bold" className="text-xl" />
                                        </div>
                                        <div>
                                            <p className="mb-0 text-secondary text-sm">Storage</p>
                                            <h6 className="fw-bold mb-0 text-dark">
                                                {subscription.planDetails?.limits?.storageGB} GB
                                            </h6>
                                        </div>
                                    </div>
                                </div>

                                <div className="border-top border-neutral-200 my-3"></div>

                                {/* Features List */}
                                <h6 className="mb-3 fw-bold text-secondary text-sm">Included Features</h6>
                                <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
                                    {(subscription.planDetails?.features || []).map((feature, idx) => (
                                        <li key={idx} className="d-flex align-items-start gap-2">
                                            <Icon icon="solar:check-circle-bold" className="text-success-main text-lg mt-1 flex-shrink-0" />
                                            <span className="text-dark fw-medium text-sm">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Renewal Modal */}
            <Modal show={showRenewModal} onHide={() => setShowRenewModal(false)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Extend Subscription</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-light p-4">
                    <div className="text-center mb-4">
                        <h4>Select a Plan to Extend</h4>
                        <p className="text-secondary">Choose your current plan or upgrade</p>
                    </div>
                    <div className="row g-4 justify-content-center">
                        {plans.map(plan => (
                            <div className="col-md-4" key={plan._id}>
                                <PlanDetailCard
                                    plan={plan}
                                    isSelected={selectedPlan?._id === plan._id}
                                    onSelect={() => setSelectedPlan(plan)}
                                    showSelectButton={false}
                                />
                                <div className="mt-3">
                                    <button
                                        onClick={() => handlePayment(plan)}
                                        disabled={!!renewingId}
                                        className="btn btn-primary w-100 radius-8 fw-medium"
                                    >
                                        {renewingId === plan._id ? 'Processing...' : `Extend ${plan.name}`}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default SubscriptionManager;
